/// <reference path="react-froala-wysiwyg/lib/index.d.ts"

import {
    Activity,
    ActivityItemFragmentFragment,
    AssignmentAttributes,
    QuizAttributes,
} from '@graphql/generated-graphql';

/**
 * Notification JSON shapeof
 * Will contain values "site" & 'email' representing what settings the current user has
 * ```json
 * {
 *  "notificatioSettings": {
 *     "activity_due_date_changed": ["site", "email"]
 *  }
 * }
 * ```
 */
export enum NotificationSettings {
    activity_due_date_changed = 'activity_due_date_changed',
    activity_due_date_missed = 'activity_due_date_missed',
    activity_help_needed = 'activity_help_needed',
    activity_marked_done = 'activity_marked_done',
    activity_needs_grading = 'activity_needs_grading',
    activity_past_due = 'activity_past_due',
    activity_started = 'activity_started',
    activity_submitted = 'activity_submitted',
    activity_reassigned = 'activity_reassigned',
    activity_reassigned_parent = 'activity_reassigned_parent',
    activity_quiz_submission_auto_graded = 'activity_quiz_submission_auto_graded',
    activity_quiz_submission_non_graded = 'activity_quiz_submission_non_graded',
    submission_annotated = 'submission_annotated',
    child_avg_grade_below_threshold = 'child_avg_grade_below_threshold',
    comment_activity = 'comment_activity',
    comment_event = 'comment_event',
    comment_to_child = 'comment_to_child',
    course_child_concluded = 'course_child_concluded',
    course_child_dropped = 'course_child_dropped',
    course_child_enrolled = 'course_child_enrolled',
    course_completed = 'course_completed',
    course_concluded = 'course_concluded',
    course_dropped = 'course_dropped',
    course_enrollment = 'course_enrollment',
    course_leader_concluded = 'course_leader_concluded',
    course_member_enrollment = 'course_member_enrollment',
    course_member_enrollment_request = 'course_member_enrollment_request',
    course_membership = 'course_membership',
    course_withdrawn = 'course_withdrawn',
    event_added = 'event_added',
    event_cancelled = 'event_cancelled',
    event_date_changed = 'event_date_changed',
    event_reminder = 'event_reminder',
    new_gradebook_entry = 'new_gradebook_entry',
    new_mention = 'new_mention',
    new_message = 'new_message',
    personal_avg_grade_below_threshold = 'personal_avg_grade_below_threshold',
    student_avg_grade_below_threshold = 'student_avg_grade_below_threshold',
    instructor_posted_in_course_chat = 'instructor_posted_in_course_chat',
    member_posted_in_course_chat = 'member_posted_in_course_chat',
}

// NOTE - keep this in sync with the backend (or expose it from the backend?)
export const NotificationsAlwaysOn = [
    NotificationSettings.new_mention,
    NotificationSettings.instructor_posted_in_course_chat,
    NotificationSettings.comment_activity,
    NotificationSettings.comment_event,
    NotificationSettings.new_gradebook_entry,
];

export const NotificationGroups = {
    direct: [
        NotificationSettings.activity_due_date_changed,
        NotificationSettings.activity_past_due,
        NotificationSettings.activity_reassigned,
        NotificationSettings.new_gradebook_entry,
        NotificationSettings.submission_annotated,
        NotificationSettings.course_completed,
        NotificationSettings.course_member_enrollment,
        NotificationSettings.course_child_enrolled,
        NotificationSettings.course_enrollment,
        NotificationSettings.course_withdrawn,
        NotificationSettings.course_child_dropped,
        NotificationSettings.course_concluded,
        NotificationSettings.course_leader_concluded,
        NotificationSettings.new_mention,
        NotificationSettings.event_reminder,
        NotificationSettings.event_added,
        NotificationSettings.event_date_changed,
        NotificationSettings.event_cancelled,
        NotificationSettings.personal_avg_grade_below_threshold,
    ],
    watching: [
        NotificationSettings.activity_started,
        NotificationSettings.activity_help_needed,
        NotificationSettings.activity_due_date_missed,
        NotificationSettings.activity_marked_done,
        NotificationSettings.activity_submitted,
        NotificationSettings.activity_needs_grading,
        NotificationSettings.activity_quiz_submission_auto_graded,
        NotificationSettings.activity_quiz_submission_non_graded,
        NotificationSettings.course_member_enrollment_request,
        NotificationSettings.course_dropped,
        NotificationSettings.course_child_concluded,
        NotificationSettings.activity_reassigned_parent,
        NotificationSettings.comment_to_child,
        NotificationSettings.student_avg_grade_below_threshold,
        NotificationSettings.child_avg_grade_below_threshold,
    ]
}

export enum CoursePermissions {
    // Course Management
    archive_delete_create_template = 'archive_delete_create_template',
    publish_unpublish = 'publish_unpublish',
    manage_roles = 'manage_roles',
    manage_parent_student_permissions = 'manage_parent_student_permissions',
    // Course Content
    manage_course_content = 'manage_course_content',
    // Course Progress
    manage_grades = 'manage_grades',
    // Communication
    view_submissions = 'view_submissions',
    reply_to_comments = 'reply_to_comments',
    message_parents_students = 'message_parents_students',
    moderate_chats_discussions = 'moderate_chats_discussions',
    // People
    manage_join_codes = 'manage_join_codes',
    manage_enrollments = 'manage_enrollments',
    // Course Settings
    manage_course_settings = 'manage_course_settings',
    manage_course_dashboard = 'manage_course_dashboard',
    manage_course_events = 'manage_course_events',
    // Parents & Students
    navigation_overview = 'navigation_overview',
    navigation_gradebook = 'navigation_gradebook',
    navigation_people = 'navigation_people',
    view_students = 'view_students',
    view_parents = 'view_parents',
    comment_course_events = 'comment_course_events',
    // Students
    message_other_students = 'message_other_students',
    student_can_skip = 'student_can_skip',
    // Parents
    message_other_parents = 'message_other_parents',
    reply_to_child_comments = 'reply_to_child_comments',
    parent_can_override = 'parent_can_override',
    parent_can_skip = 'parent_can_skip',
    unknown = 'unknown',
    // Manual Graded Course Specific
    complete_before_assigned = 'complete_before_assigned',
    enforce_seq_order = 'enforce_seq_order',
}

interface BaseAssignmentItem {
    publish: boolean;
    sourceAttributionId?: string;
    separator?: string;
    bgColor?: string;
    template?: string;
}

export interface AssignmentTextItem extends BaseAssignmentItem {
    type: 'text';
    value: string;
}

export interface AssignmentListItem extends BaseAssignmentItem {
    type: 'list';
    value: string;
}

export interface AssignmentFileItem extends BaseAssignmentItem {
    type: 'files';
    file_entry_id: string;
    other_file_entry_id?: string;
    img_classes?: string;
    img_width?: string;
}

export interface AssignmentImageItem extends BaseAssignmentItem {
    type: 'image';
    file_entry_id: string;
    other_file_entry_id?: string;
    img_classes?: string;
    img_width?: string;
}

export interface AssignmentMediaItem extends BaseAssignmentItem {
    type: 'media';
    file_entry_id: string;
    value?: string;
}

export interface AssignmentAttachmentItem extends BaseAssignmentItem {
    type: 'attachment';
    file_entry_id: string;
    value?: string;
}

export interface AssignmentEmbedItem extends BaseAssignmentItem {
    type: 'embed';
    file_entry_id: string;
    value?: string;
}

export interface AssignmenBookItem extends BaseAssignmentItem {
    type: 'bookItem';
    file_entry_id: string;
    value?: string;
}

export interface AssignmenGuideItem extends BaseAssignmentItem {
    type: 'guide';
    file_entry_id: string;
    value?: string;
}

export type AssignmentBuilderItem =
    | AssignmentTextItem
    | AssignmentListItem
    | AssignmentFileItem
    | AssignmentImageItem
    | AssignmentMediaItem
    | AssignmentAttachmentItem
    | AssignmentEmbedItem
    | AssignmenBookItem
    | AssignmenGuideItem;

export const AssignmentPredicate = {
    isTextItem: (t: AssignmentBuilderItem): t is AssignmentTextItem => {
        return t.type === 'text';
    },
    isListItem: (t: AssignmentBuilderItem): t is AssignmentListItem => {
        return t.type === 'list';
    },
    isFileItem: (t: AssignmentBuilderItem): t is AssignmentFileItem => {
        return t.type === 'files';
    },
    isImageItem: (t: AssignmentBuilderItem): t is AssignmentImageItem => {
        return t.type === 'image';
    },
    isMediaItem: (t: AssignmentBuilderItem): t is AssignmentMediaItem => {
        return t.type === 'media';
    },
    isAttachmentItem: (t: AssignmentBuilderItem): t is AssignmentAttachmentItem => {
        return t.type === 'attachment';
    },
    isEmbedItem: (t: AssignmentBuilderItem): t is AssignmentEmbedItem => {
        return t.type === 'embed';
    },
    isBookItem: (t: AssignmentBuilderItem): t is AssignmenBookItem => {
        return t.type === 'bookItem';
    },
    isGuideItem: (t: AssignmentBuilderItem): t is AssignmenGuideItem => {
        return t.type === 'guide';
    },
};

export const ItemTypePredicate = {
    isAssignment: (
        t?: Activity,
    ): t is Activity & { item: ActivityItemFragmentFragment & AssignmentAttributes } => {
        return t?.item?.type === 'activity';
    },
    isQuiz: (
        t?: Activity,
    ): t is Activity & { item: ActivityItemFragmentFragment & QuizAttributes } => {
        return t?.item?.type === 'quiz';
    },
};

export type ActivitItemType = Activity & {
    item: ActivityItemFragmentFragment & AssignmentAttributes;
};

export type QuizItemType = Activity & {
    item: ActivityItemFragmentFragment & QuizAttributes;
};

export enum AssignmentBatchItemType {
    NoVariable = 'No variable',
    AddNumberVariable = 'Add number variable',
    AddLetterVariable = 'Add letter variable',
}

export enum BatchAssignmentInterval {
    Weekly = 'Weekly',
    EveryOtherWeek = 'Every Other Week',
}
